import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { useAuth } from '@/context/AuthContext'

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────

const LandingPage        = lazy(() => import('@/pages/LandingPage'))
const LoginPage          = lazy(() => import('@/pages/LoginPage'))
const OnboardingFlow     = lazy(() => import('@/pages/OnboardingFlow'))
const CatalogPage        = lazy(() => import('@/pages/CatalogPage'))
const ContentDetailPage  = lazy(() => import('@/pages/ContentDetailPage'))
const ProfilePage        = lazy(() => import('@/pages/ProfilePage'))
const SettingsPage       = lazy(() => import('@/pages/SettingsPage'))
const AdminPage          = lazy(() => import('@/pages/AdminPage'))

// ─── Loading fallback ─────────────────────────────────────────────────────────

function LoadingFallback() {
  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bark)',
        color: 'var(--gold)',
        fontFamily: 'var(--font-serif)',
        fontSize: '1.5rem',
        letterSpacing: '0.05em',
      }}
    >
      Baobab
    </div>
  )
}

// ─── Route guards ─────────────────────────────────────────────────────────────

/**
 * Blocks access until auth state is resolved, then redirects to /login
 * if there is no session. Shows the loading fallback during the initial
 * Supabase session check to prevent a flash of the login page.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) return <LoadingFallback />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

/**
 * Redirects authenticated users away from auth pages (login, onboarding).
 * Prevents logged-in users from seeing the login screen.
 */
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) return <LoadingFallback />
  if (isAuthenticated) return <Navigate to="/browse" replace />
  return <>{children}</>
}

/**
 * Guards admin-only routes.
 * Unauthenticated users go to /login.
 * Authenticated non-admins go to /browse.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isLoading } = useAuth()

  if (isLoading) return <LoadingFallback />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isAdmin) return <Navigate to="/browse" replace />
  return <>{children}</>
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* ── Public ────────────────────────────────────────────────────── */}
        <Route path="/" element={<LandingPage />} />

        {/* ── Guest-only (redirect to /browse if already logged in) ──────── */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <GuestRoute>
              <OnboardingFlow />
            </GuestRoute>
          }
        />

        {/* ── Protected ─────────────────────────────────────────────────── */}
        <Route
          path="/browse"
          element={
            <ProtectedRoute>
              <CatalogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content/:id"
          element={
            <ProtectedRoute>
              <ContentDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />

        {/* ── Catch-all ─────────────────────────────────────────────────── */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
